import React from 'react';
import {
  Dashboard,
  People,
  Security,
  Business,
  Extension,
  WebAsset,
  BugReport,
  ListAltOutlined,
  CalendarMonthOutlined,
  Group,
  Folder,
  Home,
  Biotech,
  FactCheck,
  Settings,
  Inventory,
  LocalShipping,
  QrCode2,
  AssignmentInd,
  SummarizeOutlined,
  LibraryBooks,
  Feed,
  Contacts,
  EventBusy,
  AccessTime,
  HowToReg,
  Analytics,
  TaskAlt,
  MeetingRoom,
  Today,
  AccountBalanceWallet,
  Sick,
  LockClock,
  Payments,
  LabelOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import NavigationList, { NavSection, NavItem, NavGroup } from './NavigationList';
import {
  useNonWorkingDayPermissions,
  useProductGroupPermissions,
  useStockLocationPermissions,
  useTidsregistreringRegistrationCodePermissions,
  useTidsregistreringOverenskomstPermissions,
  useScannerPermissions,
  useInfoscreenPermissions,
} from '../../../features/auth/hooks/usePermissions';

const AppNavigation = ({ onItemClick }) => {
  const { t } = useTranslation(["common"]);

  // The Settings nav item should only show for a user who can actually
  // change something on at least one of its tabs — not just anyone who can
  // read one, which since the non-working-days merge (that tab's `canRead`
  // is now unconditionally true for any authenticated user) would otherwise
  // make this always visible regardless of permissions. Mirrors the write
  // scopes SettingsPage.jsx's own tabs are built from.
  const nonWorkingDayPermissions = useNonWorkingDayPermissions();
  const productGroupPermissions = useProductGroupPermissions();
  const stockLocationPermissions = useStockLocationPermissions();
  const registrationCodePermissions = useTidsregistreringRegistrationCodePermissions();
  const overenskomstPermissions = useTidsregistreringOverenskomstPermissions();
  const scannerPermissions = useScannerPermissions();
  const infoscreenPermissions = useInfoscreenPermissions();
  const canManageSettings = (
    nonWorkingDayPermissions.canCreate || nonWorkingDayPermissions.canUpdate
    || nonWorkingDayPermissions.canDelete || nonWorkingDayPermissions.canAdminGenerate
    || productGroupPermissions.canCreate || productGroupPermissions.canUpdate || productGroupPermissions.canDelete
    || stockLocationPermissions.canCreate || stockLocationPermissions.canUpdate || stockLocationPermissions.canDelete
    || registrationCodePermissions.canCreate || registrationCodePermissions.canUpdate || registrationCodePermissions.canDelete
    || overenskomstPermissions.canCreate || overenskomstPermissions.canUpdate || overenskomstPermissions.canDelete
    || scannerPermissions.canCreate || scannerPermissions.canUpdate || scannerPermissions.canDelete
    || infoscreenPermissions.canCreate || infoscreenPermissions.canUpdate || infoscreenPermissions.canDelete
  );

  // Ordered by how often people need it: daily work at the top, lookups in
  // the middle, manager tools and configuration at the bottom (collapsible,
  // permission-gated). Notifications, feedback and the changelog are left out
  // on purpose — the app bar bell, feedback button and the version footer
  // already lead there.
  return (
    <NavigationList onItemClick={onItemClick}>
      <NavSection id="main" showDivider={false}>
        <NavItem path="/home" label={t('common:navigation.home')} icon={<Home />} />
        <NavItem path="/dashboard" label={t('common:navigation.dashboard')} icon={<Dashboard />} permission="task:read" />
      </NavSection>

      <NavSection id="tasks" title={t('common:navigation.sections.tasks')}>
        <NavItem path="/tasks" label={t('common:navigation.tasks')} icon={<TaskAlt />} permission="task:read" />
        <NavItem path="/procedures" label={t('common:navigation.procedures')} icon={<FactCheck />} permission="procedure:read" />
        <NavItem path="/calendar" label={t('common:navigation.calender')} icon={<CalendarMonthOutlined />} permission="task:read" />
        <NavItem path="/team-calendar" label={t('common:navigation.team_calender')} icon={<Group />} permission="task:read" />
      </NavSection>

      <NavSection id="time" title={t('common:navigation.sections.time')}>
        <NavItem path="/tidsregistering/mine-dage" label={t('common:navigation.tidsregistering_mine_days')} icon={<Today />} permission="tidsregistering:day_overview:read" />
        <NavItem path="/tidsregistering/fravaer" label={t('common:navigation.tidsregistering_absence')} icon={<EventBusy />} permission="tidsregistering:absence:read" />
        <NavItem path="/tidsregistering/fremmøde" label={t('common:navigation.tidsregistering_attendance')} icon={<AccessTime />} permission="tidsregistering:day_overview:read" />
      </NavSection>

      <NavSection id="clinic" title={t('common:navigation.sections.clinic')} collapsible>
        <NavItem path="/rooms" label={t('common:navigation.rooms')} icon={<MeetingRoom />} permission="room:read" />
        <NavItem path="/equipment" label={t('common:navigation.equipment')} icon={<Biotech />} permission="equipment:read" />
        <NavItem path="/delegations" label={t('common:navigation.delegations')} icon={<AssignmentInd />} permission="delegation:read" />
        <NavItem path="/staff" label={t('common:navigation.staff_members')} icon={<People />} permission="staff:read" />
        <NavItem path="/contacts" label={t('common:navigation.contacts')} icon={<Contacts />} permission="contact:read" />
        <NavItem path="/files" label={t('common:navigation.files')} icon={<Folder />} permission="file:read" />
      </NavSection>

      <NavSection id="inventory" title={t('common:navigation.sections.inventory')} collapsible>
        <NavItem path="/inventory/items" label={t('common:navigation.inventory_items')} icon={<Inventory />} permission="inventory:item:read" />
        <NavItem path="/inventory/batches" label={t('common:navigation.inventory_batches')} icon={<QrCode2 />} permission="inventory:lot:read" />
        <NavItem path="/inventory/suppliers" label={t('common:navigation.inventory_suppliers')} icon={<LocalShipping />} permission="inventory:supplier:read" />
        <NavItem path="/inventory/msds" label={t('common:navigation.inventory_msds')} icon={<Feed />} permission="inventory:msds:read" />
      </NavSection>

      <NavSection id="management" title={t('common:navigation.sections.management')} collapsible>
        <NavItem path="/tidsregistering/godkendelser" label={t('common:navigation.tidsregistering_approvals')} icon={<HowToReg />} permission="tidsregistering:day_overview:admin_approve" />
        <NavItem path="/reports" label={t('common:navigation.reports')} icon={<SummarizeOutlined />} permission="report:generate" />
        <NavItem path="/tidsregistering/analyse" label={t('common:navigation.tidsregistering_analyse')} icon={<Analytics />} permission="tidsregistering:statistics:read" />
        <NavItem path="/tidsregistering/saldi" label={t('common:navigation.tidsregistering_balances')} icon={<AccountBalanceWallet />} permission="tidsregistering:absence:admin" />
        <NavItem path="/tidsregistering/sygefravaer" label={t('common:navigation.tidsregistering_sick_leave')} icon={<Sick />} permission="tidsregistering:sick_leave:read" />
        <NavItem path="/tidsregistering/periodelukning" label={t('common:navigation.tidsregistering_period_closing')} icon={<LockClock />} permission="tidsregistering:closing_period:read" />
        <NavItem path="/tidsregistering/loeneksport" label={t('common:navigation.tidsregistering_salary_export')} icon={<Payments />} permission="tidsregistering:closing_period:read" />
      </NavSection>

      <NavSection id="setup" title={t('common:navigation.sections.setup')} collapsible defaultExpanded={false}>
        {canManageSettings && (
          <NavItem
            path="/settings"
            label={t('common:navigation.settings')}
            icon={<Settings />}
            // No `permission` string here — visibility is the precomputed
            // canManageSettings above (an OR across every settings tab's
            // write scopes), not a single scope. The old
            // `settings:closing-days` scope this used to check was deleted
            // entirely by the lukkedage/helligdage merge migration.
          />
        )}
        <NavItem path="/categories" label={t('common:navigation.categories')} icon={<LabelOutlined />} permission="task-category:read" />
        <NavItem path="/roles" label={t('common:navigation.roles')} icon={<Security />} permission="role:read" />
      </NavSection>

      <NavSection id="system" title={t('common:navigation.sections.system')} collapsible defaultExpanded={false}>
        <NavItem path="/departments" label={t('common:navigation.departments')} icon={<Business />} permission="admin-department:read" />
        <NavItem path="/modules" label={t('common:navigation.system_modules')} icon={<Extension />} permission="admin-module:read" />
        <NavItem path="/software-licenses" label={t('common:navigation.software_licenses')} icon={<ListAltOutlined />} permission="software_license:moderate" />
        <NavItem path="/admin/templates" label={t('common:navigation.template_admin')} icon={<LibraryBooks />} permission="administration-template:read" />
        <NavGroup
          id="errorManagement"
          label={t('common:navigation.error_management')}
          icon={<BugReport />}
          permission="admin-error:read"
          defaultExpanded={true}
        >
          <NavItem path="/errors" label={t('common:navigation.dashboard')} icon={<Dashboard />} permission="admin-error:read" />
          <NavItem path="/errors/browse" label={t('common:navigation.browse')} icon={<WebAsset />} permission="admin-error:read" />
        </NavGroup>
      </NavSection>
    </NavigationList>
  );
};

export default AppNavigation;
